import { Grid, MenuItem } from "@mui/material";
import React, { useState } from "react";
import Message from "../../../../components/message";
import { MessageSections } from "../../../../components/message/message";
import BarekamText from "../../../../components/text";
import { OrderModel } from "../../../../shared/models/order/order-item.model";
import { useAppSelector } from "../../../../../redux/store";
import { OrdersTableColumn } from "../../models/column-models";
import { BarekamCard } from "../../../../components/card/card";
import { GapContainer } from "../../../../components/card/styled";
import BarekamButton, { SimpleButtons } from "../../../../components/button";
import ordersService from "../../../../../services/orders.service";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationDialog } from "../../../../components/dialogs/confirmation-dialog";
import { CardRowData } from "./row-data";
import { ThreeDot } from "../../../../components/three-dot/three-dot";
import { WarningDialog } from "../../../../components/dialogs/warning-dialog";
import QRCode from "react-qr-code";
import { OrderPaymentStatusEnum } from "../../../enums/order-payment-status.enum";
import { OrderStatesEnum } from "../../../enums/order-states.enum";

export const OrderCard = ({ order, clicked, columns, isSelected, onSelect, payButtonClicked }: OrderCardProps) => {
    const { orderStateId } = useAppSelector(state => state.ordersReducer);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [qrDialogOpen, setQrDialogOpen] = useState(false);

    const navigate = useNavigate();
    const { language } = useParams();

    async function deleteOrder() {
        setOpenConfirmationPopup(true);
    }

    async function handleConfirmationDialogClose(confirmed: boolean) {
        setOpenConfirmationPopup(false);
        if (confirmed) {
            if (order.orderId) {
                await ordersService.deleteOrderById(order.orderId)
                    .catch(error => console.log(error))
            }
        }
        navigate(`/${language}/orders/my`);
    }

    const OrderHeaderLabel = () => (
        <BarekamText variant={"1rem"} fontWeight={700}
            style={{ cursor: 'pointer' }}
            onClick={() => clicked(order)}
            width={'unset'} color={'#2066DA'}>
            {order.name || <Message messageKey={'order'} sectionId={MessageSections.ORDERS} />}
        </BarekamText>
    )

    const CardContent = () => (
        <>
            {columns.map((column, index) => {
                if (column.variant !== 'actions') {
                    return <GapContainer key={`${index}-${column.field}`}>
                        <BarekamText variant={"s"} opaque>
                            <Message messageKey={column.headerName} sectionId={MessageSections.ORDERS} />
                            {':'}
                        </BarekamText>

                        <CardRowData item={column} order={order} style={{ overflow: 'hidden' }} />
                    </GapContainer>
                } else {
                    return ''
                }
            })}
            {openConfirmationPopup && (
                <ConfirmationDialog open={openConfirmationPopup}
                    handleClose={handleConfirmationDialogClose}
                    titleKey={'delete_order'}
                    descriptionKey={'delete_confirmation_description'}
                    checkboxTitle={'save_card_info'} />
            )}
        </>
    )

    const ThreeDotAction = () => {
        return (
            <ThreeDot>
                <MenuItem onClick={deleteOrder}>
                    <Message messageKey={'delete'} sectionId={MessageSections.ORDERS} />
                </MenuItem>
            </ThreeDot>
        )
    }

    const CardHeaderAction = () => {
        if (orderStateId === OrderStatesEnum.entered) {
            return <ThreeDotAction />;
        } else if (orderStateId !== OrderStatesEnum.received) {
            if (order.paymentStatus?.paymentStatusId !== OrderPaymentStatusEnum.PAID) {
                return (
                    <BarekamButton type={SimpleButtons.LINK} onClick={() => payButtonClicked(order)}>
                        <BarekamText variant={'m'} bold>
                            <Message messageKey={'order_list_pay'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </BarekamButton>
                )
            } else if (orderStateId === OrderStatesEnum.readyForPickup) {
                return (
                    <BarekamButton type={SimpleButtons.LINK} onClick={() => setQrDialogOpen(true)}>
                        <BarekamText variant={'m'} bold>
                            <Message messageKey={'see_qr'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </BarekamButton>
                )
            }
        }
        return <></>
    }

    return (
        <Grid item>
            <BarekamCard onClick={() => onSelect(order)}
                selected={isSelected}
                headerLabel={<OrderHeaderLabel />}
                headerAction={<CardHeaderAction />}
                content={<CardContent />}
                hoverColor={'#FFF'} />

            <WarningDialog open={qrDialogOpen}
                handleClose={() => {
                    setQrDialogOpen(false);
                }}
                titleKey={'qr_dialog'}
                descriptionIcon={<QRCode style={{ height: '6rem', width: '6rem' }}
                    value={order.internalTrackingNumber} />}
                descriptionKey={'qr_dialog_description'} />
        </Grid>
    );
}

interface OrderCardProps {
    order: OrderModel;
    columns: OrdersTableColumn[],
    isSelected: boolean,
    onSelect: (order: OrderModel) => void,
    clicked: (order: OrderModel) => void,
    payButtonClicked: (order: OrderModel) => void;
}